import React, { useEffect } from "react";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { Button, Heading, Input, Paper, Text, Link } from "src/components";
import { theme } from "src/config";
import { getRoutePath, match, MatchParams, Page } from "src/routes";
import { AuthStore, RoutingStore } from "src/stores";
import toast from "react-hot-toast";

export interface IForgotPasswordProps {
  authStore?: AuthStore;
  routingStore?: RoutingStore;
  match: match<MatchParams>; // This is injected so it has to be called match
}

export const ForgotPassword = inject(
  "authStore",
  "routingStore"
)(
  observer((props: IForgotPasswordProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = React.useState<string>("");
    const [loading, setLoading] = React.useState<boolean>(false);
    const { requestForgotPassword, isLoggedIn } = props.authStore;

    useEffect(() => {
      if (isLoggedIn) {
        props.routingStore.push(`/${Page.Home}`);
      }
    }, []);

    const request = async () => {
      setLoading(true);
      const result = await requestForgotPassword(email);
      if (result) {
        toast.success(t("request_password_reset_sent"));
      } else {
        toast.error(t("request_password_reset_not_sent"));
      }
      setLoading(false);
      setEmail("");
    };

    return (
      <Paper $noShadow style={{ ...theme.paper.small, marginTop: "10vh" }} $center>
        <Box gridRowGap={theme.layout.gutter}>
          <Heading type="h2">{t("forgot_password")}</Heading>
          <Heading type="h3">{t("enter_your_email")}</Heading>
          <Text size='xsmall'>{t("forgot_password_information")}</Text>
          <Box>
            <hr />
          </Box>
          <Input
            type="email"
            label={t("email")}
            id="email"
            value={email}
            onChange={(text) => setEmail(text)}
            errorMessage={t("validation_error", { what: t("email") })}
          />
          <Box justifyContent="right">
            <Button
              variant="primary"
              size="default"
              onClick={request}
              isLoading={loading}
              disabled={!email}
            >
              {t("send")}
            </Button>
          </Box>
          <Text size="small" as="p">
              {t("go_back_to_login")}{"? "}
            <Link
              size    = "small"
              text    = {t("click_here")}
              color   = {theme.color.primary7}
              onClick = {() =>
                props.routingStore.push(getRoutePath(Page.Login))
              }
            />
          </Text>
        </Box>
      </Paper>
    );
  })
);
